import { adminActionButtonSx } from 'src/theme/adminActionButton';
import { Box, Button, CircularProgress, IconButton, Portal, Stack, Tooltip } from '@mui/material';
import { DownloadOutlined, EditOutlined } from '@mui/icons-material';
import React, { createContext, useContext } from 'react';

export const QuoteEventNameContext = createContext<(name: string, identity?: import('./quoteEventIdentity').QuoteEventIdentity) => void>(() => {});

export const QuoteNavigationActionsContext = createContext<HTMLDivElement | null>(null);

export const quoteEditorDisabledActionSx = {
  '&.Mui-disabled': {
    backgroundColor: '#f5f5f5',
    color: '#9e9e9e',
    border: '1px solid #e0e0e0'
  }
};

export const quoteEditorDraftActionSx = { height: 34, fontWeight: 700 };
export const quoteEditorApprovalActionSx = {
  width: 150, borderColor: '#000000', color: '#000000', fontWeight: 'bold',
  ...quoteEditorDisabledActionSx
};

export const quoteEditorOutlineButtonSx = {
  width: { xs: '100%', sm: 150 },
  borderColor: '#b98f00',
  backgroundColor: '#ffffff',
  color: '#000000',
  '&:hover': { borderColor: '#000000', backgroundColor: '#fff8df' },
  ...quoteEditorDisabledActionSx
};

export const quoteEditorDocumentActionSx = {
  width: { xs: '100%', sm: 150 },
  ...adminActionButtonSx,
  '& .MuiButton-startIcon': { ml: 0, mr: 1 },
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 }
};

/** Admin quote rows share one label/value grid; other document families are unchanged. */
export const quoteEditorDocumentBannerSx = {
  '& [data-document-event-details]': {
    display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', columnGap: 1, rowGap: 1
  },
  '& [data-quote-detail-row]': {
    display: 'contents',
    '& > *': { minWidth: 0, margin: 0 }
  },
  '@media screen and (min-width: 900px)': {
    '&&': { gridTemplateColumns: 'minmax(0, 12.727%) minmax(0, 29.091%) max-content minmax(0, 1fr)' },
    '& [data-quote-detail-row] .MuiTypography-root': { whiteSpace: 'nowrap', overflowWrap: 'normal' },
    '& [data-document-company-details] .MuiTypography-root:last-child': { whiteSpace: 'nowrap', overflowWrap: 'normal' },
    '& [data-banner-slot="title"] .MuiTypography-root': { typography: 'h1' }
  },
  '.client-pdf-document &': {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    '& [data-quote-detail-row] .MuiTypography-root': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
    '& [data-document-company-details] .MuiTypography-root:last-child': { whiteSpace: 'normal', overflowWrap: 'anywhere' }
  }
};

interface QuoteEditorHeaderActionsProps {
  onEditHeader: () => void;
  onDownloadPdf?: () => void;
  editDisabled?: boolean;
  downloadDisabled?: boolean;
  downloading?: boolean;
  mode?: 'all' | 'edit' | 'download';
  documentHeader?: boolean;
}

/** Stable header controls for both new and saved quote lifecycles. */
export default function QuoteEditorHeaderActions({
  onEditHeader,
  onDownloadPdf,
  editDisabled = false,
  downloadDisabled = false,
  downloading = false,
  mode = 'all',
  documentHeader = false
}: QuoteEditorHeaderActionsProps) {
  const navigationContainer = useContext(QuoteNavigationActionsContext);
  if (documentHeader && mode === 'download') {
    return (
      <Box data-testid="quote-document-download" data-pdf-exclude="true" data-html2canvas-ignore="true"
        sx={{ position: 'absolute', top: theme => theme.spacing(2), right: theme => theme.spacing(2), zIndex: 1, '@media print': { display: 'none' } }}>
        <Tooltip title="Download PDF" arrow>
          <span>
            <IconButton size="small" aria-label="Download PDF" onClick={onDownloadPdf}
              disabled={downloadDisabled || downloading || !onDownloadPdf}
              aria-busy={downloading || undefined}
              sx={{ ...adminActionButtonSx, width: 34, height: 34, minWidth: 34, borderRadius: 1 }}>
              {downloading ? <CircularProgress size={20} color="inherit" aria-label="Generating PDF" /> : <DownloadOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }
  return (
    <Portal container={navigationContainer} disablePortal={mode !== 'download' || !navigationContainer}>
    <Stack
      data-testid="shared-quote-editor-header-actions"
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent={mode === 'download' ? 'flex-end' : 'space-between'}
      spacing={1}
      sx={{
        '& .MuiButton-root': {
          width: 150,
          height: 34,
          minHeight: 34,
          py: 0,
          px: '12px',
          fontSize: 13,
          boxSizing: 'border-box',
          '& .MuiButton-startIcon': { ml: 0, mr: 1 },
          '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 }
        }
      }}
    >
      {mode !== 'download' && <Tooltip title="Edit Details" arrow>
        <span>
        <IconButton
        aria-label="Edit Details"
        onClick={onEditHeader}
        disabled={editDisabled}
        sx={{ width: 'auto', height: 34, px: 1, gap: 0.5, fontSize: 13, borderRadius: 1, color: '#E8BF21',
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'rgba(232,191,33,0.12)' } }}
      >
        <EditOutlined sx={{ fontSize: 22 }} />
        <span>Edit</span>
      </IconButton>
      </span>
      </Tooltip>}
      {mode !== 'edit' && <Button
        size="small"
        onClick={onDownloadPdf}
        startIcon={downloading ? undefined : <DownloadOutlined />}
        disabled={downloadDisabled || downloading || !onDownloadPdf}
        title={!onDownloadPdf ? 'Save the quote before downloading a PDF.' : undefined}
        sx={quoteEditorDocumentActionSx}
      >
        {downloading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          'Download PDF'
        )}
      </Button>}
    </Stack>
    </Portal>
  );
}
